import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { Box, Skeleton } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import NonIdealNew from "../../../../common/NonIdealNew";
import { ParagraphText } from "../../../../../Libraries";
import ReportPageHeader from "../ReportPageHeader";
import { FilterNonIdeal } from "../../../../common";
import pageStyles from "../styles.module.css";
import { LanguageContext } from "../../../../../context/LanguageContext";
import { useReports } from "../../../../../hooks/useReports";
import { useAuthContext } from "../../../../../context/AuthContext";
import {
  formatReportCurrency,
  getStartAndEndDate,
  REPORT_DATE_FILTERS,
  REPORT_PAGINATION_LIMIT,
  REPORT_SKELETON_ROWS,
} from "../../../../../utils";
import AppPagination from "../../../../../Libraries/AppPagination";
import dayjs from "dayjs";
import useReportExport from "../../../../../hooks/useReportExport";
import useReportFilter from "../../../../../hooks/useReportFilter";
import { analytics, triggerLogEvent } from "../../../../../config/firebase";

const TopContactsBySale = () => {
  const [I18n] = useContext(LanguageContext);
  const { handleFetchReport, loading, pageLoading, topContactsBySale, topContactsBySaleTotal } = useReports(I18n);
  const { business } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const currentDates = useRef(null);

  const fetchReport = useCallback((start, end, offset = 0) => {
    handleFetchReport({
      business_id: business?.id,
      report_type: "top_contacts_by_sale",
      start_date: dayjs(start?.toDate ? start.toDate() : start).toISOString(),
      end_date: dayjs(end?.toDate ? end.toDate() : end).toISOString(),
      limit: REPORT_PAGINATION_LIMIT,
      offset,
    });
  }, [handleFetchReport, business?.id]);


  useEffect(() => {
    triggerLogEvent(analytics, "report_top_contacts_by_sale_visited", {});
  }, []);

  const { handleExport, exportLoading } = useReportExport("top_contacts_by_sale");

  const onFetch = useCallback((start, end) => {
    currentDates.current = { start, end };
    fetchReport(start, end, 0);
  }, [fetchReport]);

  const { headerDefaultFilter, isYearlyFilter, resetCount, handleFilterDate, handleResetFilter } = useReportFilter(onFetch, "top_contacts_by_sale");

  const handleNext = useCallback((offset) => {
    if (!currentDates.current) return;
    fetchReport(currentDates.current.start, currentDates.current.end, offset);
  }, [fetchReport]);

  const showNonIdeal = isYearlyFilter && !loading && topContactsBySale.length === 0;

  const minDate = useMemo(() => dayjs().subtract(1, "year"), []);
  const maxDate = useMemo(() => dayjs().endOf("day"), []);

  return (
    <Box id="top_contacts_by_sale_page_wrapper" className={pageStyles.pageWrapper}>
      <ReportPageHeader
        title={I18n.top_contacts_by_sale}
        id="top_contacts_by_sale"
        dateFilters={REPORT_DATE_FILTERS}
        onFilterDate={handleFilterDate}
        minDate={minDate}
        maxDate={maxDate}
        defaultFilter={headerDefaultFilter}
        resetKey={resetCount}
        hideActions={showNonIdeal}
        onExport={handleExport}
        exportLoading={exportLoading}
      />

      {showNonIdeal ? (
        <Box id="top_contacts_by_sale_nonideal_body" className={pageStyles.pageBody}>
          <NonIdealNew
            buttonOneId="top_contacts_by_sale_nonideal_start_sale_btn"
            title={I18n.no_data_yet}
            subtitle={I18n.it_looks_like_you_dont_have_any_data_yet}
            button1Title={I18n.start_new_sale}
            onButton1Click={() => {
              triggerLogEvent(analytics, "report_nonideal_start_new_sale", { report_type: "top_contacts_by_sale" });
              navigate("/QuickSale/QuickSaleForm", { state: { pathname: location.pathname } });
            }}
            loading={false}
            disabledOne={false}
          />
        </Box>
      ) : (
        <>
          {(loading || topContactsBySale.length > 0) && (
            <Box id="top_contacts_by_sale_table_header" className="invoiceListTableHeader">
              <Box id="top_contacts_by_sale_header_contact" className="invListTableHeaderCol sixColumn textLeft">
                <ParagraphText id="top_contacts_by_sale_header_label_contact" className="fontSize12 fontWeight500 newLightColor">{I18n.contact}</ParagraphText>
              </Box>
              <Box id="top_contacts_by_sale_header_orders" className="invListTableHeaderCol sixColumn textLeft">
                <ParagraphText id="top_contacts_by_sale_header_label_orders" className="fontSize12 fontWeight500 newLightColor">{I18n.orders}</ParagraphText>
              </Box>
              <Box id="top_contacts_by_sale_header_last_purchased" className="invListTableHeaderCol sixColumn textLeft">
                <ParagraphText id="top_contacts_by_sale_header_label_last_purchased" className="fontSize12 fontWeight500 newLightColor">{I18n.last_purchased}</ParagraphText>
              </Box>
              <Box id="top_contacts_by_sale_header_units_sold" className="invListTableHeaderCol sixColumn textLeft">
                <ParagraphText id="top_contacts_by_sale_header_label_units_sold" className="fontSize12 fontWeight500 newLightColor">{I18n.units_sold}</ParagraphText>
              </Box>
              <Box id="top_contacts_by_sale_header_avg_order_value" className="invListTableHeaderCol sixColumn textRight">
                <ParagraphText id="top_contacts_by_sale_header_label_avg_order_value" className="fontSize12 fontWeight500 newLightColor">{I18n.avg_order_value}</ParagraphText>
              </Box>
              <Box id="top_contacts_by_sale_header_total_sales" className="invListTableHeaderCol sixColumn textRight">
                <ParagraphText id="top_contacts_by_sale_header_label_total_sales" className="fontSize12 fontWeight500 newLightColor">{I18n.total_sales}</ParagraphText>
              </Box>
            </Box>
          )}
          <Box id="topContactsScrollable" className={pageStyles.pageBody}>
            {loading ? (
              Array.from({ length: REPORT_SKELETON_ROWS }).map((_, i) => (
                <Box key={i} className="invoiceListTableBody defaultCursorImportant">
                  <Box className="invListTableHeaderCol sixColumn textLeft">
                    <Skeleton variant="rounded" animation="wave" width={100} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textLeft">
                    <Skeleton variant="rounded" animation="wave" width={32} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textLeft">
                    <Skeleton variant="rounded" animation="wave" width={90} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textLeft">
                    <Skeleton variant="rounded" animation="wave" width={40} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Skeleton variant="rounded" animation="wave" width={80} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Skeleton variant="rounded" animation="wave" width={80} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                </Box>
              ))
            ) : topContactsBySale.length === 0 ? (
              <FilterNonIdeal
                title={I18n.no_transactions_matching_those_filters}
                subTitle={`${I18n.try_adjusting_filters} `}
                subTitleText={I18n.view_yearly_data}
                subTitleTextOnClick={handleResetFilter}
                subTitleTextId="top_contacts_by_sale_filter_view_yearly"
              />
            ) : (
              <AppPagination
                data={{
                  handleNext,
                  pageLoad: pageLoading,
                  count: topContactsBySaleTotal,
                  list: topContactsBySale,
                  scrollableTarget: "topContactsScrollable",
                }}
              >
                {topContactsBySale.map(({ contact, orders, last_purchased, units_sold, avg_order_value, total_sales }, i) => (
                  <Box key={i} id={`top_contacts_by_sale_row_${i}`} className="invoiceListTableBody defaultCursorImportant">
                    <Box id={`top_contacts_by_sale_row_${i}_contact`} className="invListTableHeaderCol sixColumn textLeft">
                      <ParagraphText id={`top_contacts_by_sale_cell_${i}_contact`} className="fontSize14 newFontColor">{contact}</ParagraphText>
                    </Box>
                    <Box id={`top_contacts_by_sale_row_${i}_orders`} className="invListTableHeaderCol sixColumn textLeft">
                      <ParagraphText id={`top_contacts_by_sale_cell_${i}_orders`} className="fontSize14 newFontColor">{orders}</ParagraphText>
                    </Box>
                    <Box id={`top_contacts_by_sale_row_${i}_last_purchased`} className="invListTableHeaderCol sixColumn textLeft">
                      <ParagraphText id={`top_contacts_by_sale_cell_${i}_last_purchased`} className="fontSize14 newFontColor">
                        {last_purchased ? dayjs(last_purchased).format("D MMM, YYYY") : "—"}
                      </ParagraphText>
                    </Box>
                    <Box id={`top_contacts_by_sale_row_${i}_units_sold`} className="invListTableHeaderCol sixColumn textLeft">
                      <ParagraphText id={`top_contacts_by_sale_cell_${i}_units_sold`} className="fontSize14 newFontColor">{units_sold}</ParagraphText>
                    </Box>
                    <Box id={`top_contacts_by_sale_row_${i}_avg_order_value`} className="invListTableHeaderCol sixColumn textRight">
                      <ParagraphText id={`top_contacts_by_sale_cell_${i}_avg_order_value`} className="fontSize14 newFontColor">{formatReportCurrency(avg_order_value)}</ParagraphText>
                    </Box>
                    <Box id={`top_contacts_by_sale_row_${i}_total_sales`} className="invListTableHeaderCol sixColumn textRight">
                      <ParagraphText id={`top_contacts_by_sale_cell_${i}_total_sales`} className="fontSize14 newFontColor">{formatReportCurrency(total_sales)}</ParagraphText>
                    </Box>
                  </Box>
                ))}
              </AppPagination>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default TopContactsBySale;
